import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface Discount {
  id: number;
  discount_code: string;
  name: string;
  description: string | null;
  discount_type: 'percentage' | 'fixed_amount' | 'free_trial';
  discount_value: number;
  applies_to: 'all' | 'base_packages' | 'addons' | 'specific_packages';
  min_purchase_amount: number;
  max_discount_amount: number | null;
  start_date: string;
  end_date: string | null;
  usage_limit: number | null;
  usage_limit_per_user: number;
  current_usage_count: number;
  first_time_users_only: boolean;
  minimum_subscription_length: number | null;
  is_active: boolean;
  is_flash_sale: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  stripe_coupon_id: string | null;
  stripe_sync_status: 'pending' | 'synced' | 'error';
  last_synced_at: string | null;
  stripe_error_message: string | null;
  stripe_duration: 'once' | 'repeating' | 'forever';
  stripe_duration_in_months: number | null;
}

export function useDiscounts(includeInactive = false, includeExpired = false) {
  return useQuery({
    queryKey: ['discounts', { includeInactive, includeExpired }],
    queryFn: () => apiClient<Discount[]>(`/discounts?includeInactive=${includeInactive}&includeExpired=${includeExpired}`),
  });
}

function useInvalidateDiscounts() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['discounts'] });
}

// Uppercase enum-style values, matching what the backend's normalize* helpers accept.
export interface DiscountDraft {
  id?: number;
  code?: string;
  name?: string;
  description?: string | null;
  discount_type?: 'PERCENTAGE' | 'FIXED_AMOUNT' | 'FREE_TRIAL';
  discount_value?: number;
  applies_to?: 'ALL' | 'BASE_PACKAGES' | 'ADDONS' | 'SPECIFIC_PACKAGES';
  min_purchase_amount?: number;
  max_discount_amount?: number | null;
  start_date?: string;
  end_date?: string | null;
  usage_limit?: number | null;
  usage_limit_per_user?: number;
  first_time_users_only?: boolean;
  is_active?: boolean;
  stripe_duration?: 'ONCE' | 'REPEATING' | 'FOREVER';
  stripe_duration_in_months?: number | null;
}

export function useCreateDiscount() {
  const invalidate = useInvalidateDiscounts();
  return useMutation({
    mutationFn: (draft: DiscountDraft) => apiClient<Discount>('/discounts', { method: 'POST', body: JSON.stringify(draft) }),
    onSuccess: invalidate,
  });
}

export function useUpdateDiscount() {
  const invalidate = useInvalidateDiscounts();
  return useMutation({
    mutationFn: ({ id, ...fields }: DiscountDraft & { id: number }) =>
      apiClient<Discount>(`/discounts/${id}`, { method: 'PUT', body: JSON.stringify(fields) }),
    onSuccess: invalidate,
  });
}

export function useDeleteDiscount() {
  const invalidate = useInvalidateDiscounts();
  return useMutation({
    mutationFn: (id: number) => apiClient<{ message: string }>(`/discounts/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

interface SyncResult {
  success: boolean;
  error: string | null;
  stripeCouponId: string | null;
}

export function useSyncDiscountWithStripe() {
  const invalidate = useInvalidateDiscounts();
  return useMutation({
    mutationFn: ({ discountId, action }: { discountId: number; action: 'sync' | 'forceResync' }) =>
      apiClient<SyncResult>('/discounts/stripe-sync', { method: 'POST', body: JSON.stringify({ discountId, action }) }),
    onSuccess: invalidate,
  });
}
